import { useIntl } from 'react-intl';
import {
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@rossum/ui/material';
import { LineChartPro } from '@rossum/ui/x-charts-pro';
import { usePatchQueue } from '../../../../queues/hooks/usePatchQueue';
import { QUALITY_SPOT_CHECK_PERCENTAGE } from '../../core/utils';
import { ChartWrapper } from './charts/ChartWrapper';
import { getChartColors, getComposedChartStyles } from './charts/styles';
import {
  CHART_HEIGHT,
  getCommonGradientAreaYAxisProps,
  getCommonYAxisProps,
} from './charts/utils';

const primaryAxisId = 'primary';
const secondaryAxisId = 'secondary';

const BlurredLineChart = () => {
  const theme = useTheme();

  const { primary } = getChartColors(theme);
  const { surfaceStyles } = getComposedChartStyles({
    theme,
    primaryAxisId,
    secondaryAxisId,
  });
  return (
    <LineChartPro
      height={CHART_HEIGHT}
      dataset={[
        { x: 0, y: 9, z: 8 },
        { x: 1, y: 8.5, z: 9 },
        { x: 2, y: 9, z: 8 },
        { x: 3, y: 8, z: 8.5 },
        { x: 4, y: 9.5, z: 9.5 },
        { x: 5, y: 9, z: 8 },
      ]}
      series={[
        {
          dataKey: 'y',
          showMark: false,
          id: primaryAxisId,
          curve: 'linear',
          area: true,
        },
        {
          dataKey: 'z',
          showMark: false,
          id: secondaryAxisId,
          curve: 'linear',
          area: true,
        },
      ]}
      xAxis={[
        {
          dataKey: 'x',
          disableLine: true,
          disableTicks: true,
          tickLabelStyle: { fill: theme.palette.text.secondary },
        },
      ]}
      yAxis={[
        {
          id: primaryAxisId,
          ...getCommonGradientAreaYAxisProps(primary, { min: 0, max: 10 }),
          ...getCommonYAxisProps(theme.palette.text.secondary, { min: 0 }),
          position: 'right',
        },
      ]}
      sx={{
        ...surfaceStyles,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        filter: 'blur(3px)',
        opacity: 0.5,
        pointerEvents: 'none',
      }}
      margin={{ left: 0, right: 0 }}
    />
  );
};

type PrecisionDisabledBannerProps = { queueId: number };

/** An overlay that we show over precision chart when quality spot check is disabled */
export const PrecisionDisabledBanner = ({
  queueId,
}: PrecisionDisabledBannerProps) => {
  const intl = useIntl();
  const { mutate: patchQueue, isLoading } = usePatchQueue();

  return (
    <ChartWrapper>
      <Stack spacing={1.5} useFlexGap>
        <Typography variant="h5" color="text.secondary">
          {intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.charts.precisionRate.disabledBanner.title',
          })}
        </Typography>
        <Stack sx={{ position: 'relative' }} spacing={1.5} useFlexGap>
          <Divider />

          <Stack
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle2">
              {intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.charts.precisionRate.disabledBanner.subtitle',
              })}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 400 }}
            >
              {intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.charts.precisionRate.disabledBanner.description',
              })}
            </Typography>
          </Stack>
          <Button
            color="secondary"
            variant="outlined"
            sx={{ alignSelf: 'center' }}
            onClick={() => {
              patchQueue({
                queueId,
                payload: {
                  qualitySpotCheckPercentage: QUALITY_SPOT_CHECK_PERCENTAGE,
                },
              });
            }}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {intl.formatMessage({
              id: 'containers.settings.queues.queue.automation.charts.precisionRate.disabledBanner.button',
            })}
          </Button>

          <BlurredLineChart />
        </Stack>
      </Stack>
    </ChartWrapper>
  );
};
